import { useState, useEffect, useCallback } from "react";
import { Lock, Check, Plus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { CopyButton, GmailConfirmButton } from "@/components/settings/ForwardingAddressCard";
import AddPortalDialog, { savedMessage } from "@/components/home/AddPortalDialog";
import EditCredentialsDialog from "@/components/catalog/EditCredentialsDialog";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { BRAND } from "@/lib/constants";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Credentials edited after the latest run (or before any run): the old
// "Login failed" no longer applies until the next scheduled run re-checks.
function credsPending(p) {
  if (!p.credentials_updated_at) return false;
  if (!p.last_run_at) return true;
  return new Date(p.credentials_updated_at) > new Date(p.last_run_at);
}

const EMPTY_MANUAL = { portals: false, email: false, skipped: false };

function readManual(key) {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? { ...EMPTY_MANUAL, ...JSON.parse(raw) } : EMPTY_MANUAL;
  } catch {
    return EMPTY_MANUAL;
  }
}

function writeManual(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage unavailable: the card still works, it just won't remember
  }
}

function MarkDone({ onClick, step }) {
  return (
    <button
      type="button"
      data-testid={`mark-done-${step}`}
      onClick={onClick}
      className="ml-auto text-xs text-gray-400 underline hover:text-gray-600"
    >
      Mark as done
    </button>
  );
}

function Tick({ done }) {
  return (
    <span
      data-testid={done ? "step-done" : "step-todo"}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
        done ? "border-green-600 bg-green-600 text-white" : "border-gray-300"
      }`}
    >
      {done && <Check className="h-3 w-3" />}
    </span>
  );
}

export default function SetupCard() {
  const auth = useAuth();
  const user = auth?.user;
  const checkAuth = auth?.checkAuth;
  const [portals, setPortals] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [skipOpen, setSkipOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [savedNote, setSavedNote] = useState(null);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const storageKey = user ? `setup-card:${user.tenant_id || user.email}` : null;

  useEffect(() => {
    if (storageKey) setManual(readManual(storageKey));
  }, [storageKey]);

  const updateManual = (patch) => {
    const next = { ...manual, ...patch };
    setManual(next);
    if (storageKey) writeManual(storageKey, next);
  };

  const loadPortals = useCallback(async () => {
    try {
      const res = await api.get("/catalog/my-portals");
      setPortals(res.data?.portals || []);
    } catch {
      setPortals([]);
    }
  }, []);

  const hasUser = !!user;
  useEffect(() => {
    if (hasUser) {
      loadPortals();
    }
  }, [hasUser, loadPortals]);

  if (!user || portals === null) return null;

  const address = user.intake_email;
  const addressReady = !!address && !address.toLowerCase().endsWith("@stemmercapital.com");
  const portalsDone = portals.length > 0 || manual.portals;
  const emailDone = !!user.intake_first_email_at || manual.email;
  const doneCount = (portalsDone ? 1 : 0) + (emailDone ? 1 : 0);

  if (manual.skipped && doneCount < 2) return null;

  if (doneCount === 2) {
    return (
      <p data-testid="setup-complete" className="text-sm text-gray-600">
        You're all set — documents will appear here.
      </p>
    );
  }

  const onAdded = (portalId) => {
    if (portalId) setSavedNote(savedMessage(addressReady ? address : null));
    loadPortals();
    checkAuth?.();
  };

  return (
    <Card data-testid="setup-card">
      <CardContent className="p-5 space-y-5">
        <div className="flex items-baseline justify-between">
          <h2 className="text-base font-semibold text-gray-900">Get started</h2>
          <span className="text-xs text-gray-500">
            {doneCount} of 2 done
            <button
              type="button"
              data-testid="skip-setup"
              onClick={() => setSkipOpen(true)}
              className="ml-3 text-gray-400 underline hover:text-gray-600"
            >
              Skip setup
            </button>
          </span>
        </div>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Tick done={portalsDone} />
            <h3 className="text-sm font-medium text-gray-900">Add your portals</h3>
            {!portalsDone && <MarkDone step="portals" onClick={() => updateManual({ portals: true })} />}
          </div>
          <div className="flex flex-wrap items-center gap-2 pl-7">
            {portalsDone ? (
              <>
                {portals.map((p) => {
                  const name = p.display_name || p.canonical_name || p.portal_name;
                  const pending = credsPending(p);
                  if (p.login_failed === true && !pending && !p.is_blocked && !p.auth_paused_at) {
                    return (
                      <button
                        key={p.portal_id}
                        type="button"
                        data-testid="portal-chip"
                        onClick={() => setEditing(p)}
                        className="rounded-full bg-red-50 px-3 py-1 text-xs text-red-600 hover:bg-red-100"
                      >
                        {name}: Login failed, check your credentials
                      </button>
                    );
                  }
                  return (
                    <span key={p.portal_id} className="inline-flex items-center gap-1">
                      <span
                        data-testid="portal-chip"
                        className="rounded-full bg-gray-100 px-3 py-1 text-xs text-gray-700"
                      >
                        {name}
                      </span>
                      {pending && !p.is_blocked ? (
                        <span data-testid="creds-saved" className="text-xs text-gray-500">
                          Saved, we'll check it on the next run
                        </span>
                      ) : (
                        !p.last_run_at &&
                        !p.is_blocked && (
                          <span data-testid="waiting-first-run" className="text-xs text-gray-400">
                            Waiting for first run
                          </span>
                        )
                      )}
                    </span>
                  );
                })}
                <button
                  type="button"
                  onClick={() => setDialogOpen(true)}
                  className="inline-flex items-center gap-1 text-xs underline"
                  style={{ color: BRAND.text }}
                >
                  <Plus className="h-3 w-3" /> Add another
                </button>
                {savedNote && (
                  <p data-testid="saved-note" className="w-full text-xs text-gray-600">
                    {savedNote}
                  </p>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setDialogOpen(true)}
                className="flex w-full max-w-sm items-center gap-3 rounded-lg border-2 border-dashed border-gray-300 px-5 py-4 text-left transition-colors hover:border-gray-400"
                style={{ backgroundColor: BRAND.soft }}
              >
                <Lock className="h-6 w-6" style={{ color: BRAND.text }} />
                <span className="text-sm font-medium text-gray-900">Add a portal</span>
              </button>
            )}
          </div>
        </section>

        <section className="space-y-3">
          <div className="flex items-center gap-2">
            <Tick done={emailDone} />
            <h3 className="text-sm font-medium text-gray-900">Forward your portal login codes to us</h3>
            {!emailDone && <MarkDone step="email" onClick={() => updateManual({ email: true })} />}
          </div>
          <div className="space-y-3 pl-7">
            {addressReady ? (
              <>
                <p className="text-sm text-gray-700">
                  Add a rule in Outlook or Gmail that forwards login-code (2FA) emails from your
                  portals to this address:
                </p>
                <div className="flex items-center gap-2">
                  <code
                    data-testid="setup-address"
                    className="flex-1 min-w-0 truncate rounded-md border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm"
                  >
                    {address}
                  </code>
                  <CopyButton value={address} label="Copy address" />
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-500">Your address is being set up</p>
            )}
            {addressReady && user.gmail_forward_link && (
              <GmailConfirmButton href={user.gmail_forward_link} />
            )}
            {emailDone ? (
              <p className="text-sm font-medium text-green-600">✓ Forwarding works</p>
            ) : (
              <p className="text-sm text-gray-500">Waiting for your first email…</p>
            )}
          </div>
        </section>
      </CardContent>
      <AlertDialog open={skipOpen} onOpenChange={setSkipOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skip setup?</AlertDialogTitle>
            <AlertDialogDescription>
              You can still add portals and find your forwarding address in Settings.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => updateManual({ skipped: true })}>
              Skip setup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <EditCredentialsDialog
        portal={editing}
        open={!!editing}
        onOpenChange={(o) => {
          if (!o) setEditing(null);
        }}
        onSuccess={() => {
          setEditing(null);
          loadPortals();
        }}
      />
      <AddPortalDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        intakeEmail={addressReady ? address : null}
        onAdded={onAdded}
      />
    </Card>
  );
}
